import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import { ProjectLeadDto, ProjectLeadStatus } from "../../types/projectLeadTypes";

interface ProjectLeadsListProps {
  projectLeads: ProjectLeadDto[];
}

/**
 * Leads section rendered inside the "Alle Projekte" tab.
 *
 * Leads have a different data shape than projects (no client / coreCompetencies /
 * BT numbers yet), so they get their own compact table instead of being forced
 * into the project table. Clicking a row opens the Lead detail page.
 * Only active or discarded Leads are displayed here.
 */
const ProjectLeadsList = ({ projectLeads }: ProjectLeadsListProps) => {
  const navigate = useNavigate();
  const displayProjectLeads = projectLeads.filter(
    (projectLead) => projectLead.status === ProjectLeadStatus.Lead || projectLead.status === ProjectLeadStatus.Discarded
  );

  const renderStatusChip = (status: ProjectLeadDto["status"]) => {
    switch (status) {
      case ProjectLeadStatus.Lead:
        return <Chip size="small" label="Lead" color="info" />;
      case ProjectLeadStatus.Discarded:
        return <Chip size="small" label="Lead verworfen" color="error" variant="outlined" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  if (displayProjectLeads.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight="bold">Leads ({displayProjectLeads.length})</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Projektname</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Zeitraum</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Angelegt von</TableCell>
                  <TableCell>Angelegt am</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayProjectLeads.map((projectLead) => (
                  <TableRow
                    key={projectLead.leadId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/leads/${projectLead.leadId}`)}
                  >
                    <TableCell>{projectLead.projectName}</TableCell>
                    <TableCell>{projectLead.customer || "—"}</TableCell>
                    <TableCell>{projectLead.timeframe || "—"}</TableCell>
                    <TableCell>{renderStatusChip(projectLead.status)}</TableCell>
                    <TableCell>{projectLead.creatorName ?? `Member #${projectLead.creatorId}`}</TableCell>
                    <TableCell>{dayjs(projectLead.createdAt).format("DD.MM.YYYY")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ProjectLeadsList;
